import * as React from 'react'
import { Icon } from '@/design-system/atoms/Icon'
import { STATUS_COLORS } from '../../../constants/statusColors'
import type { SessionSummary, StatusColumnConfig } from './types'

interface KanbanCardProps {
  session: SessionSummary
  columns: StatusColumnConfig[]
  justMoved: boolean
  statusUpdating: string | null
  onNavigate: (session: SessionSummary) => void
  onStatusChange: (sessionId: string, newStatus: string) => void
}

function extractMeta(
  meta: Record<string, unknown> | undefined,
  keys: string[]
): string | undefined {
  if (!meta) return undefined
  for (const key of keys) {
    const value = meta[key]
    if (value !== null && value !== undefined && String(value).trim() !== '') {
      return String(value)
    }
  }
  return undefined
}

function formatDate(date: string | null): string {
  if (!date) return 'Sem atividade'
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(date))
}

export const KanbanCard: React.FC<KanbanCardProps> = ({
  session,
  columns,
  justMoved,
  statusUpdating,
  onNavigate,
  onStatusChange,
}) => {
  const statusColor =
    STATUS_COLORS[
      (session.status as keyof typeof STATUS_COLORS) || 'in_progress'
    ]

  const customer = extractMeta(session.metadata, ['customer', 'customer_name'])
  const serviceOrder = extractMeta(session.metadata, [
    'service_order',
    'service_order_id',
    'os',
  ])
  const plate = extractMeta(session.metadata, ['plate'])
  const vehicleModel = extractMeta(session.metadata, [
    'vehicle_model',
    'model',
    'vehicle',
  ])
  const metaBadges = [
    customer,
    serviceOrder ? `OS ${serviceOrder.replace(/^OS[-\s]?/i, '')}` : null,
    plate,
    vehicleModel,
  ].filter((b): b is string => Boolean(b))

  return (
    <div
      draggable
      onDragStart={e => {
        e.dataTransfer.effectAllowed = 'move'
        e.dataTransfer.setData('text/plain', session.id)
        e.dataTransfer.setData('status', session.status)
      }}
      onDragOver={e => e.preventDefault()}
      onClick={() => onNavigate(session)}
      className={`relative rounded-xl border p-3 shadow-sm hover:shadow-md transition cursor-pointer bg-white dark:bg-gray-900 ${
        statusColor?.activeBorder || 'border-gray-200'
      } ${
        justMoved
          ? `ring-2 ring-offset-2 ${statusColor?.activeBorder || 'ring-brand-200'} ring-offset-white dark:ring-offset-gray-900 animate-[pulse_0.6s_ease-in-out_1]`
          : ''
      }`}
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-gray-900 dark:text-white leading-tight line-clamp-2">
            {session.title || 'Conversa sem título'}
          </p>
          {session.source === 'scheduled' && (
            <span
              className="inline-flex items-center mt-1 rounded-full bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 text-[10px] font-semibold px-2 py-0.5"
              title="Criada por agendamento automático"
            >
              Agendado
            </span>
          )}
        </div>
        <span
          className={`inline-flex items-center justify-center h-5 w-5 min-w-5 min-h-5 rounded-full text-[10px] font-bold text-white ${
            statusColor?.dot || 'bg-gray-500'
          }`}
        >
          {session.messageCount}
        </span>
      </div>

      {metaBadges.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-2">
          {metaBadges.map(badge => (
            <span
              key={`${session.id}-${badge}`}
              className="inline-flex items-center px-2 py-0.5 rounded-full bg-white/90 text-[11px] text-gray-700 border border-gray-200 dark:bg-gray-800 dark:text-gray-200 dark:border-gray-700 shadow-sm max-w-full"
            >
              <span className="font-semibold text-gray-800 dark:text-gray-100 truncate max-w-[180px]">
                {badge}
              </span>
            </span>
          ))}
        </div>
      )}

      <div className="flex items-center justify-between text-[11px] text-gray-700 dark:text-gray-300">
        <span className="inline-flex items-center gap-1.5 font-semibold">
          <span
            className={`inline-flex h-2 w-2 rounded-full ${statusColor?.dot || 'bg-gray-400'}`}
          />
          {session.agentName}
        </span>
        <span className="inline-flex items-center gap-1.5 font-medium">
          <Icon name="clock" size={14} className="text-gray-500" />
          {formatDate(session.timestamp)}
        </span>
      </div>

      <div className="mt-4 md:hidden">
        <p className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 mb-2">
          Alterar status
        </p>
        <div className="flex gap-2 overflow-x-auto no-scrollbar">
          {columns
            .filter(s => s.id !== session.status)
            .map(s => (
              <button
                key={s.id}
                disabled={statusUpdating === session.id}
                onClick={e => {
                  e.stopPropagation()
                  onStatusChange(session.id, s.id)
                }}
                className={`text-[10px] px-2.5 py-1 rounded-md border font-semibold transition whitespace-nowrap ${
                  statusUpdating === session.id
                    ? 'opacity-60 cursor-wait'
                    : 'hover:-translate-y-0.5'
                } ${s.tone} bg-white/90 dark:bg-gray-900/70 shadow-sm`}
              >
                {s.label}
              </button>
            ))}
        </div>
      </div>
    </div>
  )
}
